import { Route } from '@angular/router';
import { fbsDataResolver, initialDataResolver, yaShopsResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { TelegramAuthGuard } from './core/auth/guards/telegram-auth.guard';
import { YaGuard } from './core/auth/guards/ya.guard';
import { UzumGuard } from './core/auth/guards/uzum.guard';
import { ShopGuard } from 'app/core/auth/guards/shop.guard';
import { PlanGuard } from 'app/core/auth/guards/plan.guard';
import { UzumApiKeyGuard } from 'app/core/auth/guards/uzum-api-key.guard';
import { YaKeyGuard } from './core/auth/guards/ya-key.guard';
import { RegistrationGuard } from './core/registration/guards/registration.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    {path: '', pathMatch : 'full', redirectTo: 'dashboards/home'},

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'dashboards/home'},
    {
        path: 'select-market',
        canActivate: [TelegramAuthGuard, AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {
                path: '',
                loadComponent: () => import('app/modules/select-app/select-app.component').then((c) => c.SelectAppComponent),
            }
        ]
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
            {path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes')}
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')}
        ]
    },

    // Registration routes
    {
        path: 'register',
        canActivate: [RegistrationGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: '', loadChildren: () => import('app/modules/auth/register/register.routes')}
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [TelegramAuthGuard, AuthGuard, UzumGuard],
        canActivateChild: [TelegramAuthGuard, AuthGuard, UzumGuard, PlanGuard, ShopGuard, UzumApiKeyGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
            fbs: fbsDataResolver
        },
        children: [
            {path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes')},
            {path: 'dashboards', loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes')},
            {path: 'invoices', loadChildren: () => import('app/modules/admin/invoice/invoice.routes')},
            {path: 'docs', loadChildren: () => import('app/modules/admin/tms/tms.routes')},
            {path: 'reports', loadChildren: () => import('app/modules/admin/report/report.routes')},
            {path: 'shops', loadChildren: () => import('app/modules/admin/shop/shop.routes')},
            {path: 'settings', loadChildren: () => import('app/modules/admin/settings/settings.routes')},
            {path: 'units', loadChildren: () => import('app/modules/admin/unit/unit.routes')},
            { path: 'orders', loadChildren: () => import('app/modules/admin/orders/order.routes') },
            { path: 'marketing', loadChildren: () => import('app/modules/admin/marketing/marketing.routes') },



        ]
    },
    {
        path: '',
        canActivate: [TelegramAuthGuard, AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'e-sign-in', loadChildren: () => import('app/modules/auth/e-sign-in/e-sign-in.routes')},
            {path: '403', loadChildren: () => import('app/modules/admin/error-403/error-403.routes')},
            {path: '500', loadChildren: () => import('app/modules/admin/error-500/error-500.routes')},

        ]
    },
    {
        path: 'ya',
        canActivate: [TelegramAuthGuard, AuthGuard, YaGuard],
        canActivateChild: [TelegramAuthGuard, AuthGuard, YaGuard, PlanGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
            yaData: yaShopsResolver
        },
        children: [
            {
                path: 'dashboards',
                canActivate: [YaKeyGuard],
                loadChildren: () => import('app/modules/ya/dashboard/dashboard.routes')
            },
            {
                path: 'settings',
                loadChildren: () => import('app/modules/ya/settings/settings.routes')
            },
            {
                path: 'orders',
                canActivate: [YaKeyGuard],
                loadChildren: () => import('app/modules/ya/orders/order.routes')
            },
            {path: 'reports', loadChildren: () => import('app/modules/ya/report/report.routes')},
            {path: 'units', loadChildren: () => import('app/modules/ya/unit/unit.routes')},
        ]
    },
    {path: 'maintenance', loadChildren: () => import('app/layout/common/maintenance/maintenance.routes')},
];
